// @flow

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { colorPalette, sizes, fontFamily } from '../../../styles/GlobalStyle';
import { InputText } from './InputText';

type InputCurrencyProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  onChange?: (event: string) => any,
  leftIcon?: string,
  scale?: boolean,
|};

const InputCurrencyRaw = forwardRef<InputCurrencyProps, HTMLInputElement>(
  ({ className, value, name, label, id, invalid = false, onChange, leftIcon = null, scale = false }, ref) => {
    const [formattedValue, setFormattedValue] = useState('');
    const innerRef = useRef(null);
    useImperativeHandle(ref, () => innerRef.current);

    useEffect(() => {
      const newValue =
        typeof value === 'string' ? value.replace(' €', '') : value;
      if (formattedValue !== newValue) {
        setFormattedValue(newValue);
      }
    }, [formattedValue, value]);

    const onInnerChange = useCallback(
      newValue => {
        if (onChange) {
          onChange(newValue.replace(' €', ''));
        }
        setFormattedValue(newValue.replace(' €', ''));
      },
      [onChange]
    );

    return (
      <span className={`${className || ''}${invalid ? ' invalid' : ''}`}>
        <InputText
          type="tel"
          mask={{
            extension: 'integer',
            regex: '\\d*',
            inputmode: 'tel',
            suffix: ' €',
          }}
          name={`${name}-comp`}
          id={`${id}-comp`}
          label={label}
          value={formattedValue}
          onChange={onInnerChange}
          invalid={invalid}
          leftIcon={leftIcon}
          scale={scale}
        />
        <input
          type="hidden"
          value={formattedValue}
          name={name}
          id={id}
          ref={innerRef || ref}
        />
      </span>
    );
  }
);

InputCurrencyRaw.displayName = 'InputCurrency';

export const InputCurrency: StyledComponent<
  InputCurrencyProps,
  {},
  typeof InputCurrencyRaw
> = styled(InputCurrencyRaw)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  padding-top: 40px;
  font-weight: ${fontFamily.primary.black.fontWeight};

  .top-label {
    position: absolute;
    top: 0s;
    display: block;
    width: 100%;
    text-transform: uppercase;
    transform-origin: top left;
    transition: all 400ms;
  }

  .input {
    text-align: left !important;

    &::placeholder {
      color: ${colorPalette.dark.active};
    }
  }

  .parts-wrapper {
    display: flex;
  }

  .day-part,
  .month-part {
    flex: 2;
    padding-right: 5px;
  }

  .year-part {
    flex: 6;
  }

  &.active {
    .top-label {
      transform: translateY(-30px);
    }
  }

  &.invalid {
    .top-label,
    .label {
      color: ${colorPalette.danger.active};
    }

    .field {
      &:before {
        background-color: ${colorPalette.danger.active};
      }
    }
  }
`;
